import { useState } from 'react';
import { MarketingProduct, MarketingPage } from '../types';
import { useMarketingData } from '../MarketingContext';

interface Props { onNavigate: (page: MarketingPage) => void; }

export default function ProductsPage({ onNavigate: _onNavigate }: Props) {
  const { companies, products, campaigns, content, research } = useMarketingData();
  const [search, setSearch] = useState('');
  const [filterCompany, setFilterCompany] = useState('all');
  const [selected, setSelected] = useState<MarketingProduct | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  const filtered = products.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchCompany = filterCompany === 'all' || p.companyId === filterCompany;
    return matchSearch && matchCompany;
  });

  const tabs = ['overview', 'positioning', 'personas', 'journey', 'competitors', 'content', 'seo', 'campaigns', 'research'];

  if (selected) {
    const co = companies.find(c => c.id === selected.companyId);
    const prodCampaigns = campaigns.filter(c => selected.campaignIds.includes(c.id));
    const prodContent = content.filter(c => selected.contentIds.includes(c.id));
    const prodResearch = research.filter(r => selected.researchIds.includes(r.id));

    return (
      <div className="space-y-4">
        <button onClick={() => setSelected(null)} className="text-sm text-blue-600 hover:underline">← Back to Products</button>
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{selected.name}</h2>
              <p className="text-sm text-gray-500 mt-1">{selected.description}</p>
              <div className="flex gap-2 mt-2">
                <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full">{selected.category}</span>
                <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded-full">{selected.status}</span>
                <span className="text-xs text-gray-400">🏢 {co?.name}</span>
              </div>
            </div>
          </div>

          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-4 overflow-x-auto">
            {tabs.map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap transition-colors ${activeTab === tab ? 'bg-white text-gray-900 shadow' : 'text-gray-500 hover:text-gray-700'}`}>
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">Value Proposition</h3>
                <p className="text-sm text-gray-600">{selected.valueProposition}</p>
                <h3 className="text-sm font-semibold text-gray-900">Unique Selling Point</h3>
                <p className="text-sm text-gray-600">{selected.usp}</p>
                <h3 className="text-sm font-semibold text-gray-900">Customer Problem</h3>
                <p className="text-sm text-gray-600">{selected.customerProblem}</p>
                <h3 className="text-sm font-semibold text-gray-900">Solution</h3>
                <p className="text-sm text-gray-600">{selected.solution}</p>
              </div>
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">Key Features</h3>
                <ul className="text-sm text-gray-600 space-y-1">{selected.features.map((f, i) => <li key={i}>• {f}</li>)}</ul>
                <h3 className="text-sm font-semibold text-gray-900">Customer Benefits</h3>
                <ul className="text-sm text-gray-600 space-y-1">{selected.benefits.map((b, i) => <li key={i}>• {b}</li>)}</ul>
                <h3 className="text-sm font-semibold text-gray-900">Pricing</h3>
                <p className="text-sm text-gray-600">{selected.pricing}</p>
              </div>
            </div>
          )}

          {activeTab === 'positioning' && (
            <div className="space-y-4">
              <div><h3 className="text-sm font-semibold text-gray-900 mb-1">Brand Positioning</h3><p className="text-sm text-gray-600">{selected.positioning}</p></div>
              <div><h3 className="text-sm font-semibold text-gray-900 mb-1">Marketing Messages</h3><ul className="text-sm text-gray-600 space-y-1">{selected.marketingMessages.map((m, i) => <li key={i}>• {m}</li>)}</ul></div>
              <div><h3 className="text-sm font-semibold text-gray-900 mb-1">Sales Messages</h3><ul className="text-sm text-gray-600 space-y-1">{selected.salesMessages.map((m, i) => <li key={i}>• {m}</li>)}</ul></div>
            </div>
          )}

          {activeTab === 'personas' && (
            <div className="space-y-4">
              <div><h3 className="text-sm font-semibold text-gray-900 mb-1">Target Customers</h3><p className="text-sm text-gray-600">{selected.targetCustomers}</p></div>
              <div><h3 className="text-sm font-semibold text-gray-900 mb-1">Buyer Personas</h3><div className="flex flex-wrap gap-2">{selected.buyerPersonas.map((bp, i) => <span key={i} className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg">{bp}</span>)}</div></div>
            </div>
          )}

          {activeTab === 'journey' && (
            <div><h3 className="text-sm font-semibold text-gray-900 mb-2">Customer Journey</h3><p className="text-sm text-gray-600">{selected.customerJourney}</p></div>
          )}

          {activeTab === 'competitors' && (
            <div><h3 className="text-sm font-semibold text-gray-900 mb-2">Competitors</h3><div className="flex flex-wrap gap-2">{selected.competitors.map((c, i) => <span key={i} className="text-xs px-3 py-1.5 bg-red-50 text-red-700 rounded-lg">⚔️ {c}</span>)}</div></div>
          )}

          {activeTab === 'content' && (
            <div className="space-y-2">
              {prodContent.length === 0 ? <div className="text-sm text-gray-400 text-center py-6">No content items yet</div> : prodContent.map(c => (
                <div key={c.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-800">{c.title}</div>
                  <div className="text-xs text-gray-500">{c.type} · {c.status} · {c.channel}</div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'seo' && (
            <div><h3 className="text-sm font-semibold text-gray-900 mb-2">SEO Keywords</h3><div className="flex flex-wrap gap-1.5">{selected.seoKeywords.map((kw, i) => <span key={i} className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded-full">{kw}</span>)}</div></div>
          )}

          {activeTab === 'campaigns' && (
            <div className="space-y-2">
              {prodCampaigns.length === 0 ? <div className="text-sm text-gray-400 text-center py-6">No campaigns yet</div> : prodCampaigns.map(c => (
                <div key={c.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-800">{c.name}</div>
                  <div className="text-xs text-gray-500">{c.type} · {c.status} · ₹{(c.budget / 1000).toFixed(0)}K</div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'research' && (
            <div className="space-y-2">
              {prodResearch.length === 0 ? <div className="text-sm text-gray-400 text-center py-6">No research yet</div> : prodResearch.map(r => (
                <div key={r.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-800">{r.title}</div>
                  <div className="text-xs text-gray-500">{r.type} · {r.status}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-sm text-gray-500">Product marketing — positioning, personas, journey, and strategy</p>
        </div>
      </div>
      <div className="flex gap-3 bg-white p-3 rounded-xl border border-gray-200">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products..." className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg" />
        <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
          <option value="all">All Companies</option>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(p => {
          const co = companies.find(c => c.id === p.companyId);
          return (
            <button key={p.id} onClick={() => setSelected(p)} className="bg-white border border-gray-200 rounded-xl p-5 text-left hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center text-lg">📦</div>
                <div>
                  <div className="text-sm font-bold text-gray-900">{p.name}</div>
                  <div className="text-xs text-gray-500">{co?.name} · {p.category}</div>
                </div>
              </div>
              <p className="text-xs text-gray-600 line-clamp-2 mb-3">{p.valueProposition}</p>
              <div className="flex flex-wrap gap-1">
                {p.seoKeywords.slice(0, 3).map((kw, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 bg-green-50 text-green-600 rounded">{kw}</span>)}
              </div>
              <div className="flex gap-1.5 mt-3">
                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{p.campaignIds.length} campaigns</span>
                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{p.contentIds.length} content</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
